/**
 * Reading MUI X Data Grid v8's row-selection model.
 *
 * The model is `{ type: 'include' | 'exclude', ids: Set<GridRowId> }`, and the second variant
 * is the one that catches people out: clicking the header checkbox produces an **exclude**
 * model, where `ids` holds the rows that are *not* selected. An empty exclude set therefore
 * means "everything is selected", not "nothing is".
 *
 * `ProgramacionPagosResult.tsx` has its own copy of this logic that reads `.ids` without
 * looking at `type`. On an exclude model it returns the excluded rows, so selecting everything
 * with the header checkbox reports zero selected. That file is left alone here -- fixing it is
 * a separate change with its own testing -- but do not copy from it; use this instead.
 */
import type { GridRowSelectionModel } from '@mui/x-data-grid';

/**
 * Return the ids actually selected.
 *
 * `idsVisibles` must be every row the grid currently shows *after filtering*, in the same id
 * space as the selection model. It is only consulted for an exclude model, where the selection
 * is defined by subtraction and cannot be known from the model alone.
 */
export function idsSeleccionados(
  modelo: GridRowSelectionModel | null | undefined,
  idsVisibles: readonly (string | number)[],
): (string | number)[] {
  if (!modelo) return [];

  // Older MUI versions handed over a plain array. Kept so this helper does not become the
  // reason an upgrade or a downgrade breaks.
  if (Array.isArray(modelo)) return modelo as (string | number)[];

  const crudos = (modelo as any).ids;
  const conjunto: Set<any> =
    crudos instanceof Set ? crudos : new Set(Array.isArray(crudos) ? crudos : []);

  if ((modelo as any).type === 'exclude') {
    return idsVisibles.filter((id) => !conjunto.has(id));
  }
  return Array.from(conjunto) as (string | number)[];
}

/** An empty selection, for resetting the model. */
export const SELECCION_VACIA: GridRowSelectionModel = {
  type: 'include',
  ids: new Set(),
} as GridRowSelectionModel;
